/**
 * Consume/View API endpoints
 *
 * These endpoints handle the public viewing experience
 */

import { AxiosError } from 'axios';
import { apiClient, keepaliveRequest, stampAxiosCauseTrace, type KeepaliveRequest } from './client';

export { causeTraceIdOf } from './client';
import { reportClientError } from './clientErrors';
import { recordLeaveMarker, type LeaveSave } from './pagehideMarker';
import { isAccountMergedRetry } from './failureClass';
import { chargeBeaconBytes, keepaliveBodyBytes, keepaliveBudgetRemaining } from './keepaliveBudget';
import { convertKeysToCamel } from '@/lib/dataTransform';
import { z } from 'zod';

// Schema for a 403/401 access-denied body, matching vingapi's
// consume/shared/exceptions.py contract: `{error, message, login_or_signup_as?}`
// wrapped in FastAPI's `{"detail": {...}}` convention (unwrapped before parsing,
// see getViewData). Only `authentication_required` ever sends `login_or_signup_as`.
//
// vingapi's `resolution: identity_reconciliation_pending` is deliberately NOT
// read. It reported a D3 misalignment as the cause of the block, but vingapi
// 45d8cf57 ("derive access from identity, not from bookkeeping") made an
// ASSIGNED share reachable from ANY contact holding a verified method of this
// person — so a misalignment can no longer cause this 403, and the token can
// only fire on denials reconciliation will never clear. Its removal is in
// flight on the backend; parsing is non-strict, so an unexpected key is
// ignored either way.
/**
 * The remedy an assigned-content denial may carry (vingapi
 * `consume/shared/schemas.py::VerifyMethodOffer`): "you were denied, and here
 * is the address that would unblock this — prove you own it."
 *
 * vingapi only ever names a method the REQUEST itself supplied (the `?email=`
 * / `?phone=` param, or the recipient a short-link click verified onto the
 * session), that sits on a contact holding this share's live assignment, and
 * that is not already verified on this account. So the value is safe to echo
 * back and verifying it is guaranteed to open the content — the client does
 * not re-derive any of that, it just drives add-method → verify.
 *
 * The key is OMITTED, not null, when there is nothing to offer.
 */
const VerifyMethodOfferSchema = z.object({
  kind: z.literal('verify_method'),
  methodType: z.enum(['email', 'phone']),
  method: z.string().min(1),
});

const AccessDeniedSchema = z.object({
  error: z.enum([
    'authentication_required',
    'assignment_required',
    'content_not_ready',
    'preview_not_authorized',
    'preview_auth_required',
  ]),
  message: z.string(),
  loginOrSignupAs: z.string().nullable().optional(),
  // `.catch(undefined)` and not a bare `.optional()`: a future `kind` we don't
  // know how to drive must degrade to "no offer", never fail the whole object
  // and collapse a perfectly good denial into the `unknown` fallback — the
  // mistake the reverted `resolution` token made (identity-frontend-plan §5).
  offer: VerifyMethodOfferSchema.optional().catch(undefined),
});
import type {
  ViewResponse,
  ViewUpdatePayload,
  AccessDeniedResponse,
} from '@/types';
import type { FileRef, FileRefError } from '@/types/file';
import { extractFileDecline } from '@/features/files/declines';

// Result type for getViewData - either success or access denied
// NOTE (audit M15/M4): this module once carried the legacy Angular-era
// consume surface (submitAnswers, submitChecklist, markComponentComplete,
// per-file upload endpoints, persistProgress/persistAnswers, ...). None of
// it had production callers — viewers use the generic updateViewState and
// uploadFileToS3 — so it was removed; recover from git history if an
// endpoint gets re-adopted.

export type ViewDataResult =
  | { success: true; data: ViewResponse }
  | { success: false; accessDenied: AccessDeniedResponse };

/**
 * @flow_step
 * @touches Assignment:create PacketView:create ConsumeSession:create
 * @upstream UI:ViewContainer
 * @downstream consume.assignment.pipeline.consume.PacketConsumePipeline
 * @intent Fetches packet/checklist state for the viewer via share stub
 * @intent Server creates viewer-initiated assignment if none exists (required=false, sentiment=GRAY)
 * @intent Server creates PacketView (visit tracking) and ConsumeSession on each GET
 * @intent 403/401 returns structured AccessDeniedResponse (not thrown)
 * @intent URL params forwarded for tracking (email, token, etc.)
 */
export async function getViewData(
  stub: string,
  params?: Record<string, string | number | undefined>
): Promise<ViewDataResult> {
  try {
    const response = await apiClient.get<ViewResponse>(`/api/view/shares/${stub}/`, { params });
    return { success: true, data: response.data };
  } catch (error) {
    if (error instanceof AxiosError && (error.response?.status === 403 || error.response?.status === 401)) {
      // vingapi wraps structured denial bodies in FastAPI's `{"detail": {...}}`
      // convention (consume/shared/exceptions.py). Fall back to the raw body
      // itself if `detail` is absent or a plain string (FastAPI's default
      // HTTPException shape is `{"detail": "Forbidden"}`) — the schema parse
      // below fails closed into the generic fallback either way.
      const rawData = error.response.data as { detail?: unknown } | undefined;
      const rawDetail = rawData?.detail ?? rawData;
      const camel = convertKeysToCamel(rawDetail);
      const parsed = AccessDeniedSchema.safeParse(camel);
      if (parsed.success) {
        return {
          success: false,
          accessDenied: {
            code: parsed.data.error,
            message: parsed.data.message,
            loginOrSignupAs: parsed.data.loginOrSignupAs ?? null,
            offer: parsed.data.offer ?? null,
          },
        };
      }
      console.warn('[api/consume] 403/401 body did not match AccessDeniedSchema:', parsed.error.issues);
      return {
        success: false,
        accessDenied: {
          code: 'unknown',
          message: 'You don’t have permission to view this content.',
        },
      };
    }
    // Re-throw other errors (404, 500, network errors, etc.)
    throw error;
  }
}

/**
 * What every consume view PUT carries besides its payload (vingreact#715,
 * vingapi#634 W3/W4). REQUIRED on both writers so the compiler finds every
 * call site.
 *
 * - `engaged`: the page's presence signal at SEND time (ViewContainer's one
 *   `usePresence()`), sent as the body's `engaged` so the server can tell a
 *   person from a link scanner.
 * - `viewParams`: the same query map ViewContainer builds for the GET (the
 *   `?claim` token already stripped; `link` = the recipient-link path segment
 *   when the page URL has one, vingreact#717), sent as the PUT's query params
 *   so the write resolves the viewer exactly as the read did.
 */
export interface ViewWriteContext {
  engaged: boolean;
  viewParams: Record<string, string>;
  /**
   * Send through `fetch(..., { keepalive: true })` so the request survives the
   * page unloading (vingreact#715 634c): only the tab-departure flush sets it.
   * Absent/false = the default axios transport.
   */
  keepalive?: boolean;
  /**
   * Which viewer writes (vingreact#989 wire gate): a checklist's writes carry
   * no componentId and share ONE gate key per stub (`${stub}:checklist`).
   * Absent = a packet write, keyed by its componentId.
   */
  surface?: 'packet' | 'checklist';
  /**
   * vingreact#1029: the consume key (one person, one assignment) the page
   * belongs to, read at SEND time. Sent as the body's `expected_consume_key`
   * only when it is a non-empty string; null / undefined / '' = the field is
   * absent (a signed-out page, #133), never `null`.
   */
  expectedConsumeKey?: string | null;
}

/**
 * vingreact#1029: the ONE rule for the wire's `expected_consume_key`: present
 * only for a non-empty string. Every body this file builds (the PUT builder,
 * the hidden-keepalive commit draft, the commit beacon) spreads this.
 * Returned in camelCase; the PUT transports snake-case it, the beacon's hand-built
 * body uses `expectedConsumeKeyField(key, 'snake')`.
 */
function expectedConsumeKeyField(key: string | null | undefined): { expectedConsumeKey?: string };
function expectedConsumeKeyField(key: string | null | undefined, style: 'snake'): { expected_consume_key?: string };
function expectedConsumeKeyField(
  key: string | null | undefined,
  style?: 'snake',
): { expectedConsumeKey?: string } | { expected_consume_key?: string } {
  if (typeof key !== 'string' || key === '') return {};
  return style === 'snake' ? { expected_consume_key: key } : { expectedConsumeKey: key };
}

/** A built consume view PUT: what either transport sends. */
type ViewWriteRequest = KeepaliveRequest & { params: Record<string, string>; data: ViewUpdatePayload & Record<string, unknown> };

/**
 * The one builder for a consume view PUT (URL, body incl. `engaged`, query
 * params), shared by both transports and by `resetAnswers`. Case conversion
 * and auth are the transport's job (the axios interceptor / `keepaliveRequest`,
 * which use the same `convertKeysToSnake` and stored-token helper).
 */
function buildViewWriteRequest(
  stub: string,
  payload: ViewUpdatePayload,
  ctx: ViewWriteContext,
): ViewWriteRequest {
  return {
    method: 'PUT',
    url: `/api/view/shares/${stub}/`,
    data: { ...payload, engaged: ctx.engaged, ...expectedConsumeKeyField(ctx.expectedConsumeKey) },
    params: ctx.viewParams,
  };
}

// ---------------------------------------------------------------------------
// Wire gate (vingreact#989 W1): one consume state PUT in flight per component
// ---------------------------------------------------------------------------

/**
 * The previous request on each gate key, as a promise that SETTLES (never
 * rejects) when that request settles. Module state, not hook state: a
 * remounted viewer, the Redo reset, the checklist submit, the keepalive
 * transport and the axios transport all queue behind the same entry. An entry
 * is removed once the last request on its key settles.
 */
const writeGate = new Map<string, Promise<void>>();

/**
 * The AbortController of every consume state PUT that has STARTED and not
 * settled, by stub. The reconnect path aborts them (a hung request must not
 * stall the page after the network comes back); see
 * `abortConsumeWritesInFlight`.
 */
const inFlightWrites = new Map<string, Set<AbortController>>();

/**
 * vingreact#989 S5b K2: what each consume state PUT that has STARTED and not
 * settled actually put on the wire, by stub: the final request object (with
 * `engaged` from the write context, after the persist layer's reshaping) and
 * the transport that carries it (after the R5 keepalive→axios fallback and the
 * K4 budget check). The pagehide pass resends the commits riding axios, the
 * one transport a leaving page cancels.
 */
interface StartedWrite {
  req: ViewWriteRequest;
  transport: 'axios' | 'keepalive';
}
const startedWrites = new Map<string, Set<StartedWrite>>();

/**
 * vingreact#989 S5b K3: the objects (a recorded request, a queued payload, the
 * source of a hidden-tab draft) the leave passes have already handled. A
 * bfcache restore followed by another pagehide sends none of them again, and
 * never blocks a different write.
 */
const handledOnPagehide = new WeakSet<object>();
const draftedOnHidden = new WeakSet<object>();

/**
 * vingreact#989 S5b: a queued payload the pagehide pass sent by keepalive,
 * with that request's outcome. If the page survives (bfcache, a same-document
 * test), the persist drain adopts this outcome for the payload instead of
 * sending the same write a second time; adopted once, then forgotten.
 */
const pagehideCopies = new WeakMap<object, Promise<ViewResponse>>();

/**
 * vingreact#989 r736 K1c: a request on the wire on axios that a leave pass
 * (K1a' at pagehide, K6b at hidden) copied by keepalive, with the copy's
 * outcome. If the original then rejects (an unload cancel, a reconnect abort,
 * a network error) and its copy LANDED, the original's caller gets the copy's
 * response: one outcome reaches the persist layer, and a write that is on the
 * server is not re-queued, stranded or bannered. Keyed by the recorded request
 * itself, so only a copy of that same request is ever adopted (never the K6
 * equal-seq draft sent for a commit).
 */
const inflightCopies = new WeakMap<ViewWriteRequest, Promise<ViewResponse>>();

/**
 * The gate key for a consume state PUT: `${stub}:${componentId}`, a checklist
 * write `${stub}:checklist`, a packet write with no componentId
 * `${stub}:packet`. Decided by the payload being sent.
 */
export function consumeWriteGateKey(
  stub: string,
  payload: Pick<ViewUpdatePayload, 'componentId'>,
  surface: ViewWriteContext['surface'],
): string {
  if (surface === 'checklist') return `${stub}:checklist`;
  return payload.componentId != null ? `${stub}:${payload.componentId}` : `${stub}:packet`;
}

/**
 * Run `start` once the previous request on `key` has settled (success or
 * failure). With nothing ahead it starts SYNCHRONOUSLY, so a keepalive send
 * from a pagehide handler reaches `fetch` before the handler returns.
 */
function throughGate<T>(key: string, start: () => Promise<T>): Promise<T> {
  const ahead = writeGate.get(key);
  const run = ahead ? ahead.then(start) : start();
  const settled = run.then(() => undefined, () => undefined);
  writeGate.set(key, settled);
  void settled.then(() => {
    if (writeGate.get(key) === settled) writeGate.delete(key);
  });
  return run;
}

/**
 * Abort every consume state PUT for `stub` that is on the wire right now
 * (vingreact#989 U3: the offline→online transition). The aborted request
 * rejects as a cancel (`axios.isCancel`), which is not a slow failure and
 * shows no banner; its payload stays queued and the reconnect resume resends
 * it at once. Requests still waiting behind the gate are not touched.
 */
export function abortConsumeWritesInFlight(stub: string): number {
  const controllers = inFlightWrites.get(stub);
  if (!controllers) return 0;
  const count = controllers.size;
  for (const controller of [...controllers]) controller.abort();
  return count;
}

/** Gate keys with a request in flight or queued. For the test-start guard. */
export function consumeWriteGateKeys(): string[] {
  return [...writeGate.keys()];
}

/** Test teardown: forget every gate entry, in-flight controller and started write. */
export function resetConsumeWriteGate(): void {
  writeGate.clear();
  inFlightWrites.clear();
  startedWrites.clear();
}

async function sendViewWrite(
  stub: string,
  gateKey: string,
  req: ViewWriteRequest,
  keepalive: boolean,
  pagehideCopy?: Promise<ViewResponse>,
): Promise<ViewResponse> {
  return throughGate(gateKey, async () => {
    // S5b: this write already left by keepalive at pagehide; its outcome is this write's.
    if (pagehideCopy) return pagehideCopy;
    const controller = new AbortController();
    let controllers = inFlightWrites.get(stub);
    if (!controllers) {
      controllers = new Set();
      inFlightWrites.set(stub, controllers);
    }
    controllers.add(controller);
    let started = startedWrites.get(stub);
    if (!started) {
      started = new Set();
      startedWrites.set(stub, started);
    }
    let record: StartedWrite | undefined;
    try {
      // S5b K4: a body over the keepalive budget would be refused by the
      // browser after the fact; it stays on axios.
      if (keepalive && keepaliveBodyBytes(req.data) <= keepaliveBudgetRemaining()) {
        let pending: Promise<ViewResponse> | undefined;
        const sendKeepalive = () => keepaliveRequest<ViewResponse>({ ...req, signal: controller.signal });
        try {
          pending = sendKeepalive();
        } catch (err) {
          // R5: fetch threw synchronously (unsupported / body refused up front).
          // Best effort: this flush goes out on the default transport instead.
          console.warn('[api/consume] keepalive fetch unavailable, falling back to axios:', err);
        }
        if (pending) {
          record = { req, transport: 'keepalive' };
          started.add(record);
          try {
            return await pending;
          } catch (err) {
            // #1336 R5: the keepalive path skips the axios interceptors, so the
            // one `account_merged_retry` resend is made here, in the same gate
            // slot, with the same body (same save_seq). A second one throws.
            // Awaited inside the gate slot, so a queued second write cannot
            // interleave. The resend's bytes count against the keepalive budget
            // like any keepalive send (keepaliveRequest charges them while in
            // flight; the first send's bytes were released when it settled).
            if (!isAccountMergedRetry(err) || controller.signal.aborted) throw err;
            return await sendKeepalive();
          }
        }
      }
      record = { req, transport: 'axios' };
      started.add(record);
      try {
        const response = await apiClient.put<ViewResponse>(req.url, req.data, { params: req.params, signal: controller.signal });
        return stampAxiosCauseTrace(response);
      } catch (err) {
        // r736 K1c: the original failed. If a leave pass copied this request,
        // its copy decides (awaited if still pending): landed ⇒ its response
        // is this write's; failed too ⇒ the original's error, as before.
        const copy = inflightCopies.get(req);
        if (!copy) throw err;
        return await copy.catch(() => { throw err; });
      }
    } finally {
      controllers.delete(controller);
      if (controllers.size === 0 && inFlightWrites.get(stub) === controllers) inFlightWrites.delete(stub);
      if (record) started.delete(record);
      if (started.size === 0 && startedWrites.get(stub) === started) startedWrites.delete(stub);
    }
  });
}

// Update view state (persist progress, answers, etc.)
export async function updateViewState(
  stub: string,
  payload: ViewUpdatePayload,
  ctx: ViewWriteContext,
): Promise<ViewResponse> {
  const pagehideCopy = pagehideCopies.get(payload);
  pagehideCopies.delete(payload);
  return sendViewWrite(
    stub,
    consumeWriteGateKey(stub, payload, ctx.surface),
    buildViewWriteRequest(stub, payload, ctx),
    ctx.keepalive === true,
    pagehideCopy,
  );
}

// ---------------------------------------------------------------------------
// Leaving the page (vingreact#989 S5b): pending writes leave by keepalive
// ---------------------------------------------------------------------------

/** What the page hands the leave passes; everything else they read from the started-write record. */
export interface KeepaliveLeaveInput {
  /**
   * The persist queue's entries that are not on the wire, in queue order,
   * each exactly as the persist layer would send it now (its gates applied:
   * a frozen (S4) component's remainder, nothing durable while read-only, the
   * seq stamped). The pass sends these objects; it never re-reads the page.
   */
  queued: ReadonlyArray<ViewUpdatePayload>;
  /** Presence and view params at the moment of leaving (no `keepalive` needed). */
  ctx: ViewWriteContext;
  /** A component whose commit must not leave (frozen by S4, read-only). */
  skipCommit: (componentId: number) => boolean;
}

/** One affected component, for the ops report (K7). */
interface KeepaliveTooLarge {
  componentId: number;
  bodyBytes: number;
  budgetRemaining: number;
  inflight: boolean;
  beaconSent: boolean;
}

export const KEEPALIVE_TOO_LARGE_REPORT_NAME = 'ConsumeKeepaliveTooLarge';
export const KEEPALIVE_TOO_LARGE_REPORT_KIND = 'consume_keepalive_too_large';
const KEEPALIVE_TOO_LARGE_MESSAGE = 'Consume write over the keepalive budget when the page was leaving';

/**
 * K7: one report per affected component, by beacon (the page is leaving).
 * No answer content. vingapi records this kind at WARN (vingapi#1592).
 */
function reportKeepaliveTooLarge(
  stub: string,
  surface: ViewWriteContext['surface'],
  phase: 'pagehide' | 'hidden',
  entries: KeepaliveTooLarge[],
): void {
  for (const e of entries) {
    reportClientError({
      error: KEEPALIVE_TOO_LARGE_MESSAGE,
      name: KEEPALIVE_TOO_LARGE_REPORT_NAME,
      source: 'consume',
      kind: KEEPALIVE_TOO_LARGE_REPORT_KIND,
      transport: 'beacon',
      context: {
        reason: 'keepalive_too_large',
        surface: surface ?? 'packet',
        share_stub: stub,
        component_id: e.componentId,
        body_bytes: e.bodyBytes,
        budget_remaining: e.budgetRemaining,
        inflight: e.inflight,
        beacon_sent: e.beaconSent,
        phase,
      },
    });
  }
}

const isCommitWrite = (data: ViewUpdatePayload): boolean => data.action === 'commit';

/**
 * The Redo answer reset on the wire (`answers: {}`, the explicit reset
 * contract; the same shape `isAnswerReset` names in features/consume). r736
 * r2.5: an in-flight reset is never copied (no vingapi pin covers a duplicate
 * of that shape); a QUEUED one still leaves at pagehide like any entry (K1b).
 */
const isAnswerResetWrite = (data: ViewUpdatePayload): boolean => {
  const { answers } = data;
  return answers !== undefined && answers !== null && typeof answers === 'object' && Object.keys(answers).length === 0;
};

/**
 * r736 K1a'/K4: the order a leave pass charges the keepalive budget in:
 * commits, then answer drafts (writes carrying `questions`), then the rest
 * (visits, media, pages).
 */
function leaveRank(data: ViewUpdatePayload): 0 | 1 | 2 {
  if (isCommitWrite(data)) return 0;
  return Array.isArray(data.questions) ? 1 : 2;
}

/** This stub's commits on the wire right now, as they went out. */
function commitsOnTheWire(stub: string): StartedWrite[] {
  return [...(startedWrites.get(stub) ?? [])].filter((w) => isCommitWrite(w.req.data));
}

/**
 * r736 K1a'/K6b: this stub's writes on the wire on AXIOS right now (the one
 * transport a leaving page cancels), as recorded (K2), the Redo reset excluded.
 */
function copyableOnTheWire(stub: string): StartedWrite[] {
  return [...(startedWrites.get(stub) ?? [])].filter((w) => w.transport === 'axios' && !isAnswerResetWrite(w.req.data));
}

/**
 * vingreact#989 S5b K1 (widened by r736 K1a'): the page is going away
 * (`pagehide`). Send, now and in one pass, every write that would otherwise
 * die with it, through keepalive and OUTSIDE the wire gate and the persist
 * mutex (a keepalive send queued behind an XHR the unload cancels never
 * starts):
 *   a. every consume state PUT on the wire on axios (commit, answer draft,
 *      visit, media; never the Redo reset), resent as the exact recorded
 *      request: equal seq only with identical bytes, never rebuilt (vingapi
 *      #1505 contract);
 *   b. every queued entry, built by `buildViewWriteRequest` as the gated path
 *      would build it.
 * Order and budget charge: commits, then answer drafts, then the rest (in
 * flight before queued within each). Duplicates are safe: an identical
 * equal-seq copy is a no-op whichever lands first (vingapi Q1, #1176,
 * `test_1505_equal_seq_identical_resend_is_idempotent`). Results are not read
 * here (the page is gone); if the page survives, a queued entry's outcome is
 * kept for the drain, an in-flight one's for its original (K1c).
 *
 * K4/K5: a write over the remaining keepalive budget is not sent. A QUEUED
 * commit over it gets the commit beacon instead (`beaconCommit`, the last
 * saved draft is committed: the pre-S5 outcome); a commit ON THE WIRE over it
 * gets nothing (a beacon racing that PUT runs completion twice, vingapi#1591);
 * an answer draft ON THE WIRE over it gets nothing either (a beacon would
 * commit-grade older answers). Each of those: one K7 report. A visit / media
 * / pages write over it: nothing, no report. K3: nothing handled once is
 * handled again, except a copy that failed (r2.1: the next pass copies again).
 */
export function sendPagehideKeepalive(
  stub: string,
  input: KeepaliveLeaveInput & {
    /** K5: fire the commit beacon for this component; whether it was accepted. */
    beaconCommit: (componentId: number) => boolean;
    /** vingreact#1337: who is leaving, for the page-close marker (null: nobody signed in). */
    viewerId?: number | null;
  },
): void {
  const tooLarge: KeepaliveTooLarge[] = [];
  // vingreact#1337: the answer saves this pass handled, for the page-close marker.
  const marked: LeaveSave[] = [];
  const mark = (data: ViewUpdatePayload, path: LeaveSave['path']): void => {
    if (data.componentId != null && Array.isArray(data.questions) && typeof data.saveSeq === 'number') {
      marked.push({ component_id: data.componentId, save_seq: data.saveSeq, path });
    }
  };
  const candidates: Array<{ source: object; req: ViewWriteRequest; inflight: boolean }> = [
    ...copyableOnTheWire(stub).map((w) => ({ source: w.req, req: w.req, inflight: true })),
    ...input.queued.map((p) => ({ source: p, req: buildViewWriteRequest(stub, p, input.ctx), inflight: false })),
  ].sort((a, b) => leaveRank(a.req.data) - leaveRank(b.req.data));
  for (const { source, req, inflight } of candidates) {
    if (handledOnPagehide.has(source)) continue;
    const rank = leaveRank(req.data);
    const componentId = req.data.componentId;
    if (rank === 0 && componentId != null && input.skipCommit(componentId)) continue;
    const bodyBytes = keepaliveBodyBytes(req.data);
    const budgetRemaining = keepaliveBudgetRemaining();
    if (bodyBytes > budgetRemaining) {
      mark(req.data, 'too_large');
      // A visit / media / pages write over the budget is not reported; a queued one stays queued.
      if (rank === 0 && componentId != null) {
        handledOnPagehide.add(source);
        const beaconSent = inflight ? false : input.beaconCommit(componentId);
        tooLarge.push({ componentId, bodyBytes, budgetRemaining, inflight, beaconSent });
      } else if (rank === 1 && inflight && componentId != null) {
        handledOnPagehide.add(source);
        tooLarge.push({ componentId, bodyBytes, budgetRemaining, inflight, beaconSent: false });
      }
      continue;
    }
    handledOnPagehide.add(source);
    let sent: Promise<ViewResponse>;
    try {
      sent = keepaliveRequest<ViewResponse>({ method: req.method, url: req.url, data: req.data, params: req.params });
    } catch {
      // No keepalive transport on this engine: nothing else survives a leaving page.
      handledOnPagehide.delete(source);
      mark(req.data, 'no_transport');
      continue;
    }
    mark(req.data, 'keepalive');
    sent.catch(() => { handledOnPagehide.delete(source); });
    if (inflight) inflightCopies.set(req, sent);
    else pagehideCopies.set(source, sent);
  }
  // Written once the pass has decided each save's path, in the same task as the sends.
  recordLeaveMarker(stub, input.viewerId ?? null, marked);
  reportKeepaliveTooLarge(stub, input.ctx.surface, 'pagehide', tooLarge);
}

/**
 * The tab went hidden (`visibilitychange`). Hidden is not leaving (the axios
 * requests keep going), but iOS may freeze or kill a hidden tab with no
 * pagehide, and 736b departs this way. Two sends, each once, by keepalive,
 * outside the gate:
 *
 * vingreact#989 S5b K6: each commit queued or on the wire gets a DRAFT with
 * the SAME `questions` array and the same `save_seq` taken from the captured
 * commit (never re-read from the page), no `action`. Before the commit it
 * applies at seq N and the commit at N still grades; after it, equal seq with
 * identical values changes nothing (vingapi#1561). Its response is not read
 * and never adopted (K1c). Over the budget: not sent, one K7 report.
 *
 * r736 K6b: each NON-commit on the wire on axios (answer drafts first, then
 * visits / media; never the Redo reset) gets its recorded request once,
 * marked in the same K3 set as pagehide (one copy per request per page life;
 * a failed copy is unmarked, r2.1), its outcome kept for the original (K1c).
 * The bytes of every commit still on the wire are RESERVED first (r2.2), so
 * these copies never push the pagehide commit copy over the budget. Over the
 * budget: not sent, no report (the XHR is still alive; pagehide decides).
 */
export function sendHiddenKeepalive(stub: string, input: KeepaliveLeaveInput): void {
  const tooLarge: KeepaliveTooLarge[] = [];
  const send = (req: ViewWriteRequest, data: ViewWriteRequest['data']): Promise<ViewResponse> | undefined => {
    try {
      const sent = keepaliveRequest<ViewResponse>({ method: req.method, url: req.url, data, params: req.params });
      sent.catch(() => undefined);
      return sent;
    } catch {
      // No keepalive transport: the axios requests are still going.
      return undefined;
    }
  };

  const commitSources: Array<{ source: object; req: ViewWriteRequest; inflight: boolean }> = [
    ...commitsOnTheWire(stub).map((w) => ({ source: w.req, req: w.req, inflight: true })),
    ...input.queued.filter(isCommitWrite).map((p) => ({ source: p, req: buildViewWriteRequest(stub, p, input.ctx), inflight: false })),
  ];
  for (const { source, req, inflight } of commitSources) {
    const { componentId, questions, saveSeq, engaged, expectedConsumeKey } = req.data;
    if (componentId == null || !Array.isArray(questions)) continue;
    if (draftedOnHidden.has(source) || input.skipCommit(componentId)) continue;
    draftedOnHidden.add(source);
    const data: ViewUpdatePayload & Record<string, unknown> = {
      componentId,
      questions,
      ...(saveSeq !== undefined ? { saveSeq } : {}),
      engaged,
      ...expectedConsumeKeyField(typeof expectedConsumeKey === 'string' ? expectedConsumeKey : undefined),
    };
    const bodyBytes = keepaliveBodyBytes(data);
    const budgetRemaining = keepaliveBudgetRemaining();
    if (bodyBytes > budgetRemaining) {
      tooLarge.push({ componentId, bodyBytes, budgetRemaining, inflight, beaconSent: false });
      continue;
    }
    void send(req, data);
  }

  const onAxios = copyableOnTheWire(stub);
  let reserved = 0;
  for (const w of onAxios) {
    const componentId = w.req.data.componentId;
    if (leaveRank(w.req.data) !== 0 || handledOnPagehide.has(w.req)) continue;
    if (componentId != null && input.skipCommit(componentId)) continue;
    reserved += keepaliveBodyBytes(w.req.data);
  }
  const copies = onAxios
    .filter((w) => leaveRank(w.req.data) !== 0)
    .sort((a, b) => leaveRank(a.req.data) - leaveRank(b.req.data));
  for (const { req } of copies) {
    if (handledOnPagehide.has(req)) continue;
    if (keepaliveBodyBytes(req.data) > keepaliveBudgetRemaining() - reserved) continue;
    handledOnPagehide.add(req);
    const sent = send(req, req.data);
    if (!sent) {
      handledOnPagehide.delete(req);
      continue;
    }
    sent.catch(() => { handledOnPagehide.delete(req); });
    inflightCopies.set(req, sent);
  }
  reportKeepaliveTooLarge(stub, input.ctx.surface, 'hidden', tooLarge);
}

// ---------------------------------------------------------------------------
// Checklist submission status (vingapi#1523 S1, vingreact#989 S2s)
// ---------------------------------------------------------------------------

/** Whether the viewer's own checklist submission has been submitted. */
export interface ViewSubmissionStatus {
  id: number;
  isComplete: boolean;
  /** When it was submitted (UTC ISO); null while it is open. */
  submittedDate: string | null;
}

/**
 * The status read's own timeout. The route is one indexed read on the writer
 * (no locks, no writes), so a healthy answer is milliseconds; the 120 s
 * default would leave the learner on "Submitting..." for two minutes after a
 * 504 that already cost them 30 s. A read that times out is "outcome unknown"
 * to the caller (today's copy; the next Submit asks again).
 */
export const SUBMISSION_STATUS_TIMEOUT_MS = 15_000;

const SubmissionStatusSchema = z.object({
  id: z.number().int(),
  isComplete: z.boolean(),
  submittedDate: z.string().nullable().optional(),
});

/**
 * @flow_step
 * @upstream UI:ChecklistViewer
 * @downstream consume.shared.routers.get_submission_status
 * @intent After a checklist submit failed slowly (timeout / slow 504), ask
 *   whether the submit landed anyway. Read-only on the server: no session,
 *   visit or submission is created.
 *
 * `viewerParams` must be exactly the query the consume GET sent (`link`,
 * `email`, `phone`, ...): the route resolves the viewer from the same inputs.
 * Not through the write gate. Throws on any failure, including both 404s
 * (`Submission not found`, `Share not found`, see
 * `isSubmissionStatusMiss`) and a 200 whose body does not parse.
 */
export async function getViewSubmissionStatus(
  stub: string,
  submissionId: number,
  viewerParams: Record<string, string>,
): Promise<ViewSubmissionStatus> {
  const response = await apiClient.get<unknown>(
    `/api/view/shares/${stub}/submissions/${submissionId}/`,
    { params: viewerParams, timeout: SUBMISSION_STATUS_TIMEOUT_MS },
  );
  const parsed = SubmissionStatusSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`submission status returned an invalid body: ${parsed.error.message}`);
  }
  return {
    id: parsed.data.id,
    isComplete: parsed.data.isComplete,
    submittedDate: parsed.data.submittedDate ?? null,
  };
}

// ---------------------------------------------------------------------------
// Answer-file acceptance (file-handling plan A2/D2/D6)
// ---------------------------------------------------------------------------

export interface AcceptConsumeFileInput {
  /** Staging key returned by the stub-scoped presign (`uploadFileToS3`). */
  fileKey: string;
  /** Original filename with extension (matches the key). */
  filename: string;
  /** The short-answer question the upload answers. */
  questionId: number;
}

/**
 * The accept decision. `accepted: true` carries what the answer must send
 * (`{id: fileId, claim}`, exactly once) and the FileRef the chip renders from;
 * `accepted: false` is the server's 422 decline — `code` is a `FileRefError`
 * and `message` the user-facing wording to show inline. Any other failure
 * (network, 5xx, 404 question) throws.
 */
export type AcceptConsumeFileResult =
  | { accepted: true; fileId: number; file: FileRef; claim: string }
  | { accepted: false; code: FileRefError | string; message: string };

const AcceptFileResponseSchema = z.object({
  fileId: z.number().int(),
  claim: z.string().min(1),
  file: z.object({ status: z.string() }).passthrough(),
});

/**
 * @flow_step
 * @touches File:create
 * @upstream UI:QuestionDisplay
 * @downstream consume.shared.routers.consume_accept_file
 * @intent The learner's one-shot acceptance request between the S3 upload and
 *   the chip appearing: 200 → {file_id, file, claim}; 422 {code, message} → declined
 * @intent Never authors a FileRef; the answer sends {id, claim} once, then echoes
 */
export async function acceptConsumeFile(
  stub: string,
  input: AcceptConsumeFileInput,
): Promise<AcceptConsumeFileResult> {
  try {
    const response = await apiClient.post<unknown>(
      `/api/view/shares/${encodeURIComponent(stub)}/files/accept/`,
      { fileKey: input.fileKey, filename: input.filename, questionId: input.questionId },
    );
    const parsed = AcceptFileResponseSchema.safeParse(response.data);
    if (!parsed.success) {
      throw new Error(`accept endpoint returned an invalid body: ${parsed.error.message}`);
    }
    return {
      accepted: true,
      fileId: parsed.data.fileId,
      claim: parsed.data.claim,
      file: parsed.data.file as unknown as FileRef,
    };
  } catch (error) {
    // The decline body is top-level `{code, message}` (FileDeclinedError.to_dict),
    // the same shape every upload surface returns — see features/files/declines.
    const decline = extractFileDecline(error);
    if (decline) {
      return { accepted: false, code: decline.code, message: decline.message };
    }
    throw error;
  }
}

/** The claim-exchange answer (vingapi `POST /api/shorturl/claim/`). */
export interface ClaimExchangeResponse {
  status: 'staged' | 'bound' | 'anonymous';
  /** Null when what staged is durable (a live recipient link, vingapi#1123 S1). */
  expiresAt?: string | null;
}

/**
 * @flow_step
 * @touches ShortUrlClaim:update
 * @upstream UI:ViewContainer
 * @downstream content.share.shorturl_routers.shorturl_claim_exchange
 * @intent Two-phase exchange for the `?claim=` token and/or the recipient-link
 *   path segment (vingapi#1123 contract 2)
 *
 * The server model is `extra=forbid`: `token` and `link` are each OMITTED,
 * never null, when the page URL does not carry them. Key order keeps the
 * claim-only body byte-identical to the pre-1123 `{token, phase}`.
 */
export async function exchangeClaim(
  phase: 'stage' | 'complete',
  { token, link }: { token: string | null; link?: string },
): Promise<ClaimExchangeResponse> {
  const body = {
    ...(token !== null ? { token } : {}),
    phase,
    ...(link ? { link } : {}),
  };
  const res = await apiClient.post<ClaimExchangeResponse>('/api/shorturl/claim/', body);
  return res.data;
}

// Same origin the axios client targets (empty in prod → relative same-origin
// path; VITE_API_URL is set only for local dev / tests). sendBeacon bypasses
// axios, so the URL is built here rather than through apiClient.
const BEACON_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * On-leave COMMIT beacon (spec §5 Layer 2 / §13). Fires the SAME idempotent
 * commit as Next/Done for the active draft-complete UNCOMMITTED questions
 * component when the learner leaves the tab (pagehide / visibilitychange
 * `hidden`), where a normal awaited PUT can't reliably complete on a tab-kill.
 * Fire-and-forget: no response is read. The server idle-sweeper (Layer 1)
 * backstops a dropped beacon, and the endpoint no-ops on an already-committed /
 * partial component, so a redundant beacon is harmless.
 *
 * FROZEN wire — MUST match the backend track EXACTLY:
 *   navigator.sendBeacon(
 *     '/api/view/shares/{stub}/commit-beacon',
 *     new Blob([JSON.stringify({ email, component_id, engaged, link? })], { type: 'text/plain' }),
 *   )
 * `text/plain` is the beacon-safe content-type the endpoint parses; the
 * same-origin consume session cookie rides along (credentials), and `email`
 * disambiguates the magic-link recipient carried in the body. `engaged` is the
 * page's presence signal at send time and `link` the recipient-link path
 * segment (vingapi#1123 contract addendum, PR 1137: the beacon has the same
 * person gate as the PUT — `link` is honoured only with `engaged: true`; the
 * gate is the server's, the client sends both regardless). `link` is OMITTED,
 * never null, when the page URL has no segment. Returns the sendBeacon success
 * bool (false when queued failed or the API is unavailable).
 */
export function beaconCommit(
  stub: string,
  { email, componentId, engaged, link, expectedConsumeKey }: {
    email?: string | null;
    componentId: number;
    engaged: boolean;
    link?: string;
    /** vingreact#1029: the page's consume key; omitted from the body when null. */
    expectedConsumeKey?: string | null;
  },
): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    return false;
  }
  const url = `${BEACON_BASE_URL}/api/view/shares/${stub}/commit-beacon`;
  const body = new Blob(
    [JSON.stringify({
      email: email ?? null,
      component_id: componentId,
      engaged,
      ...(link ? { link } : {}),
      ...expectedConsumeKeyField(expectedConsumeKey, 'snake'),
    })],
    { type: 'text/plain' },
  );
  try {
    const queued = navigator.sendBeacon(url, body);
    // vingreact#989 S5b K4: a beacon draws on the page's keepalive quota.
    if (queued) chargeBeaconBytes(body.size);
    return queued;
  } catch {
    // sendBeacon can throw on some engines (e.g. body too large); the sweeper
    // still catches the abandonment, so never let a beacon failure surface.
    return false;
  }
}
export const RESET_ANSWERS: ReadonlyArray<unknown> = Object.freeze([]);

/**
 * Reset all answers for a component (retry flow).
 *
 * Bug-hunt #14: uses the explicit `RESET_ANSWERS` sentinel rather
 * than an inline `[]` so the intent is documented and the migration
 * to a dedicated backend endpoint (uptake #3846) is local to this
 * function.
 */
export async function resetAnswers(
  stub: string,
  componentId: number,
  ctx: ViewWriteContext,
): Promise<ViewResponse> {
  // F7: wire body sends `answers` as an empty OBJECT (matching submitAnswers).
  return sendViewWrite(
    stub,
    consumeWriteGateKey(stub, { componentId }, ctx.surface),
    buildViewWriteRequest(stub, { componentId, answers: {} }, ctx),
    ctx.keepalive === true,
  );
}
